import React, { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import App from 'containers/App';
import { matterRoutes } from 'routes/matterRoutes';
import { reportingRoutes } from 'routes/reportingRoutes';
import { aiAssistantRoutes } from 'routes/aiAssistantRoutes';
import { projectTeamRoutes } from 'routes/projectTeamRoutes';
import { solutionRoutes } from 'routes/solutionRoutes';
import { accountSettingsRoutes } from 'routes/accountSettingsRoutes';
import { ProtectedRouteGuard } from 'guard/ProtectedRouteGuard';
import { profileSettingsRoutes } from 'routes/profileSettingsRoutes';
import { indexRoutes, NotFoundPage } from 'routes/indexRoutes';
import RouteAnalytics from 'routes/RouteAnalytics';
import { asyncRouteLoader } from './utils/asyncRouteLoader';

const Home = asyncRouteLoader(
  () => import('containers/Home'),
  [
    {
      type: 'reducer',
      reducerKey: 'home',
      importPromise: () => import('containers/Home/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Home/sagas'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Solutions/sagas'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/ProjectTeams/sagas'),
    },
  ]
);
const SuperAdmin = asyncRouteLoader(
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  () => import('containers/SuperAdmin'),
  [
    {
      type: 'reducer',
      reducerKey: 'superAdmin',
      importPromise: () => import('containers/SuperAdmin/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/SuperAdmin/sagas'),
    },
  ]
);

const AccountManagement = React.lazy(
  () =>
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    import('pages/app/domain/management/[account_uuid]')
);
const Inbox = asyncRouteLoader(
  () => import('containers/Inbox'),
  [
    {
      type: 'reducer',
      reducerKey: 'inbox',
      importPromise: () => import('containers/Inbox/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/Inbox/sagas'),
    },
  ]
);
const Databases = lazy(() => import('pages/app/databases'));

const AppRoutes = () => (
  <Routes>
    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
    {/* @ts-ignore */}
    <Route path="/" element={<App />}>
      <Route index element={<Navigate to="/app" replace />} />
      {/* Base path: / - public routes (mostly) */}
      {indexRoutes()}

      {/* Base path: /app - protected application routes */}
      <Route
        path="app"
        element={
          <ProtectedRouteGuard>
            <Home />
          </ProtectedRouteGuard>
        }
      >
        {/* Inbox - index redirects to inbox */}
        <Route index element={<Navigate to="inbox" replace />} />
        <Route
          path="inbox"
          element={
            <RouteAnalytics pageName="Inbox">
              <Inbox />
            </RouteAnalytics>
          }
        />
        {/* Project Teams */}
        {projectTeamRoutes()}

        {/* Solutions */}
        {solutionRoutes()}

        {/* Databases */}
        <Route
          path="databases"
          element={
            <RouteAnalytics pageName="Databases">
              <Databases />
            </RouteAnalytics>
          }
        />

        {/* AI Assistant */}
        {aiAssistantRoutes()}

        {/* Matters */}
        {matterRoutes()}

        {/* Reporting */}
        {reportingRoutes()}

        {/* Super Admin */}
        <Route
          path="superadmin"
          element={
            <RouteAnalytics pageName="SuperAdmin">
              <SuperAdmin />
            </RouteAnalytics>
          }
        />

        {/* Account Management - access via SuperAdmin */}
        <Route
          path="domain/management/:account_uuid"
          element={<AccountManagement />}
        />

        {/* Account Settings */}
        {accountSettingsRoutes()}

        {/* Profile Settings */}
        {profileSettingsRoutes()}
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<NotFoundPage />} />
    </Route>
  </Routes>
);

export default AppRoutes;
